import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, orderBy, getDocs, where, increment, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

import { notifyAdmins, sendNotification } from '../../utils/notificationService';
import { CheckSquare, Search, Check, X, Loader2, AlertCircle, FileEdit, Download, Medal, MessageSquare, Play, Star, Eye, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../context/ToastContext';

export default function AdminAssignments() {
  const { addToast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assignment'); // 'assignment' or 'final_project'
  const [searchTerm, setSearchTerm] = useState('');

  const [gradingModalData, setGradingModalData] = useState(null);
  const [grade, setGrade] = useState('');
  const [adminFeedback, setAdminFeedback] = useState('');
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'submissions'), orderBy('submittedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSubmissions(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleOpenGrading = (sub) => {
    setGradingModalData(sub);
    setGrade(sub.grade || '');
    setAdminFeedback(sub.adminFeedback || '');
    setSelectedBadges(sub.awardedBadges?.filter(b => ['golden_hand', 'designers_eye', 'inspirer'].includes(b)) || []);
  };

  const handleCloseGrading = () => {
    setGradingModalData(null);
    setGrade('');
    setAdminFeedback('');
    setSelectedBadges([]);
  };

  const toggleBadge = (badgeId) => {
    setSelectedBadges(prev => prev.includes(badgeId) ? prev.filter(b => b !== badgeId) : [...prev, badgeId]);
  };

  const handleAction = async (statusOverride) => {
    if (!gradingModalData) return;
    if (statusOverride === 'graded' && grade === '') {
      addToast('يجب إدخال الدرجة لاعتمادها.', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const newStatus = statusOverride;
      let finalBadges = [...selectedBadges];
      let earnedPixels = 0;

      if (newStatus === 'graded' && grade !== '') {
        const numGrade = Number(grade);
        earnedPixels = Math.floor(numGrade / 10);
        if (numGrade === (gradingModalData.maxScore || 100)) {
          if (!finalBadges.includes('perfect_pixel')) finalBadges.push('perfect_pixel');
        }
      }

      await updateDoc(doc(db, 'submissions', gradingModalData.id), {
        status: newStatus,
        grade: grade !== '' ? Number(grade) : null,
        adminFeedback: adminFeedback.trim(),
        earnedPixels: newStatus === 'graded' ? earnedPixels : 0,
        awardedBadges: newStatus === 'graded' ? finalBadges : [],
        gradedAt: new Date()
      });

      // Update student pixels and check pixel_collector
      if (newStatus === 'graded' && grade !== '') {
        try {
          const userRef = doc(db, 'users', gradingModalData.studentId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const currentPixels = userSnap.data().pixels || 0;
            const currentBadges = userSnap.data().badges || [];
            if (currentPixels + earnedPixels >= 100 && !currentBadges.includes('pixel_collector') && !finalBadges.includes('pixel_collector')) {
              finalBadges.push('pixel_collector');
            }
            const updatePayload = { pixels: increment(earnedPixels) };
            if (finalBadges.length > 0) {
              updatePayload.badges = arrayUnion(...finalBadges);
            }
            await updateDoc(userRef, updatePayload);
          }
        } catch (err) {
          console.error("Failed to update student pixels", err);
        }
      }
      // If graded, also save grade to student's enrollment for course-level aggregation
      if (newStatus === 'graded' && grade !== '' && gradingModalData.courseId) {
        try {
          const enrQ = query(
            collection(db, 'enrollments'),
            where('userId', '==', gradingModalData.studentId),
            where('courseId', '==', gradingModalData.courseId)
          );
          const enrSnap = await getDocs(enrQ);
          if (!enrSnap.empty) {
            const enrollmentRef = doc(db, 'enrollments', enrSnap.docs[0].id);
            const assignmentKey = gradingModalData.assignmentId || gradingModalData.id;
            await updateDoc(enrollmentRef, {
              [`assignmentGrades.${assignmentKey}`]: {
                grade: Number(grade),
                maxScore: gradingModalData.maxScore || 100,
                assignmentName: gradingModalData.assignmentName,
                gradedAt: new Date()
              }
            });
          }
        } catch (enrErr) {
          console.error('Failed to update enrollment grade:', enrErr);
        }
      }
      // Notify the student
      await sendNotification(gradingModalData.studentId,
        newStatus === 'graded' ? 'تم تقييم تكليفك' : 'مطلوب تعديل على التكليف',
        `تم مراجعة: ${gradingModalData.assignmentName}`,
        newStatus === 'graded' ? 'success' : 'warning',
        `/dashboard/assignments`
      );
      addToast(newStatus === 'graded' ? 'تم اعتماد الدرجة بنجاح' : 'تم إرسال طلب التعديل للطالب', 'success');
      handleCloseGrading();
    } catch (e) {
      addToast('حدث خطأ أثناء العملية', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = submissions.filter(s => {
    const kind = s.assignmentType || 'assignment';
    if (kind !== activeTab) return false;
    const q = searchTerm.toLowerCase();
    return (s.studentName?.toLowerCase().includes(q) || s.assignmentName?.toLowerCase().includes(q));
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <span className="px-3 py-1 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 rounded-full font-bold text-xs">قيد المراجعة</span>;
      case 'graded': return <span className="px-3 py-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 rounded-full font-bold text-xs">مكتمل</span>;
      case 'needs_revision': return <span className="px-3 py-1 bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400 rounded-full font-bold text-xs">بحاجة لتعديل</span>;
      default: return null;
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2 flex items-center gap-3">
            <CheckSquare className="w-8 h-8 text-primary-500" />
            إدارة التكاليف والمشاريع
          </h1>
          <p className="text-slate-500 dark:text-zinc-400">راجع تكاليف الطلاب، قدم التقييم، أو اطلب تعديلات.</p>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-3xl shadow-xl mb-6 p-4 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="flex bg-slate-100 dark:bg-black/30 p-1 rounded-xl w-full md:w-auto">
          <button onClick={() => setActiveTab('assignment')}
            className={`flex-1 md:w-32 py-2 rounded-lg font-bold text-md transition-all flex items-center justify-center gap-2 ${activeTab === 'assignment' ? 'bg-white dark:bg-zinc-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300'}`}>
            <FileEdit className="w-4 h-4" /> تكاليف
          </button>
          <button onClick={() => setActiveTab('final_project')}
            className={`flex-1 md:w-40 py-2 rounded-lg font-bold text-md transition-all flex items-center justify-center gap-2 ${activeTab === 'final_project' ? 'bg-white dark:bg-zinc-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300'}`}>
            <Medal className="w-4 h-4" /> مشاريع التخرج
          </button>
        </div>

        <div className="relative w-full md:w-72">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
          <input type="text" placeholder="البحث باسم الطالب أو التكليف..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-11 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white font-bold" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-primary-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 text-slate-500 bg-white/50 dark:bg-zinc-900/40 backdrop-blur-3xl rounded-3xl border border-slate-200/50 dark:border-white/10">
          <AlertCircle className="w-16 h-16 text-slate-300 dark:text-zinc-700 mb-4" />
          <p className="text-xl font-bold">لا توجد تسليمات في هذا القسم.</p>
        </div>
      ) : (
        <div className="bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl border border-slate-200/50 dark:border-white/10 rounded-3xl shadow-xl overflow-hidden hidden md:block">
          <table className="w-full text-right border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-zinc-800/50 border-b border-slate-200/50 dark:border-white/10 text-slate-500 dark:text-zinc-400">
                <th className="px-6 py-4 font-bold">الطالب</th>
                <th className="px-6 py-4 font-bold">الدورة / التكليف</th>
                <th className="px-6 py-4 font-bold text-center">الحالة</th>
                <th className="px-6 py-4 font-bold text-center">تاريخ التسليم</th>
                <th className="px-6 py-4 font-bold text-center w-32">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {filtered.map(sub => (
                <tr key={sub.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                  <td className="px-6 py-4 font-bold text-slate-900 dark:text-white">
                    {sub.studentName || 'طالب مجهول'}
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-bold text-primary-600 dark:text-primary-400">{sub.assignmentName}</p>
                    <p className="text-xs text-slate-500 dark:text-zinc-500 font-medium">{sub.courseTitle || 'الدورة'}</p>
                  </td>
                  <td className="px-6 py-4 text-center">{getStatusBadge(sub.status)}</td>
                  <td className="px-6 py-4 text-slate-500 dark:text-zinc-400 font-medium text-center" dir="ltr">
                    {sub.submittedAt?.toDate ? sub.submittedAt.toDate().toLocaleDateString('en-GB') : '-'}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button onClick={() => handleOpenGrading(sub)}
                      className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-white font-bold hover:bg-slate-200 dark:hover:bg-white/10 transition-colors">
                      تقييم
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile list view omitted for brevity, but could easily be mapped as cards if needed */}

      {/* Grading Modal */}
      <AnimatePresence>
        {gradingModalData && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseGrading} />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative w-full max-w-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b border-slate-200/50 dark:border-white/5 flex items-center justify-between sticky top-0 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md z-10">
                <h2 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-2">تقييم تسليم: {gradingModalData.studentName}</h2>
                <button onClick={handleCloseGrading} className="p-2 text-slate-400 hover:text-rose-500 bg-slate-100 dark:bg-white/5 rounded-full transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 overflow-y-auto space-y-6">

                <div className="bg-slate-50 dark:bg-black/30 p-4 rounded-2xl border border-slate-200 dark:border-white/5">
                  <h3 className="font-bold text-slate-700 dark:text-zinc-300 mb-4 flex items-center gap-2"><CheckSquare className="w-5 h-5 text-primary-500" /> الملفات المرفقة</h3>

                  {gradingModalData.driveLink && (
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-zinc-800 rounded-xl border border-slate-200 dark:border-white/10 mb-3">
                      <div className="flex items-center gap-3 w-full">
                        <span className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center shrink-0"><CheckSquare className="w-5 h-5 text-blue-500" /></span>
                        <a href={gradingModalData.driveLink} target="_blank" rel="noreferrer" className="flex-1 font-bold text-blue-600 dark:text-blue-400 truncate hover:underline" dir="ltr">{gradingModalData.driveLink}</a>
                      </div>
                    </div>
                  )}

                  {gradingModalData.files && gradingModalData.files.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {gradingModalData.files.map((file, i) => (
                        <a key={i} href={file.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 bg-white dark:bg-zinc-800 rounded-xl border border-slate-200 dark:border-white/10 hover:border-primary-500 transition-colors group">
                          {file.url.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                            <img src={file.url} alt="att" className="w-10 h-10 rounded-lg object-cover border border-slate-200 dark:border-white/5" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-rose-50 dark:bg-rose-500/10 flex items-center justify-center shrink-0">
                              <Download className="w-5 h-5 text-rose-500" />
                            </div>
                          )}
                          <span className="font-bold text-sm text-slate-700 dark:text-zinc-200 line-clamp-1 group-hover:text-primary-500" dir="ltr">{file.name || 'ملف مرفق'}</span>
                        </a>
                      ))}
                    </div>
                  )}

                  {!gradingModalData.driveLink && (!gradingModalData.files || gradingModalData.files.length === 0) && (
                    <p className="text-slate-400 text-center font-medium">لم يتم إرفاق ملفات.</p>
                  )}
                </div>

                {/* Student Note Display */}
                {gradingModalData.studentNote && (
                  <div className="bg-amber-50/50 dark:bg-amber-500/5 p-4 rounded-2xl border border-amber-200/50 dark:border-amber-500/20">
                    <h3 className="font-bold text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-amber-600 dark:text-amber-500" /> ملاحظة الطالب
                    </h3>
                    <p className="text-slate-700 dark:text-zinc-300 text-sm font-medium leading-relaxed whitespace-pre-wrap">
                      {gradingModalData.studentNote}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">الدرجة ({gradingModalData.maxScore || 100} كحد أقصى)</label>
                    <input type="number" min="0" max={gradingModalData.maxScore || 100} value={grade} onChange={e => setGrade(e.target.value)} placeholder="مثال: 95" className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white font-bold" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2">حالة التسليم الحالية</label>
                    <div className="py-2.5">{getStatusBadge(gradingModalData.status)}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-slate-400" /> تعليق الإدارة والملاحظات</label>
                  <textarea value={adminFeedback} onChange={e => setAdminFeedback(e.target.value)} rows="3" placeholder="ملاحظات لتوجيه الطالب..." className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-slate-900 dark:text-white font-medium resize-none"></textarea>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-zinc-300 mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" /> منح شارات إبداعية
                  </label>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium mb-3">اختر الشارات التي يستحقها العمل لإبراز تميّز الطالب.</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button 
                      onClick={() => toggleBadge('golden_hand')}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all ${selectedBadges.includes('golden_hand') ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-500 text-amber-700 dark:text-amber-400 shadow-sm' : 'bg-slate-50 dark:bg-zinc-800/50 border-slate-200 dark:border-white/10 text-slate-500 hover:border-slate-300 dark:hover:border-white/20'}`}
                    >
                      <Star className={`w-6 h-6 mb-2 ${selectedBadges.includes('golden_hand') ? 'fill-amber-500 text-amber-500' : ''}`} />
                      <span className="font-bold text-sm">اليد الذهبية</span>
                    </button>

                    <button 
                      onClick={() => toggleBadge('designers_eye')}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all ${selectedBadges.includes('designers_eye') ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-500 text-emerald-700 dark:text-emerald-400 shadow-sm' : 'bg-slate-50 dark:bg-zinc-800/50 border-slate-200 dark:border-white/10 text-slate-500 hover:border-slate-300 dark:hover:border-white/20'}`}
                    >
                      <Eye className={`w-6 h-6 mb-2 ${selectedBadges.includes('designers_eye') ? 'text-emerald-500' : ''}`} />
                      <span className="font-bold text-sm">عين المصمم</span>
                    </button>

                    <button 
                      onClick={() => toggleBadge('inspirer')}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all ${selectedBadges.includes('inspirer') ? 'bg-indigo-50 dark:bg-indigo-500/10 border-indigo-500 text-indigo-700 dark:text-indigo-400 shadow-sm' : 'bg-slate-50 dark:bg-zinc-800/50 border-slate-200 dark:border-white/10 text-slate-500 hover:border-slate-300 dark:hover:border-white/20'}`}
                    >
                      <Sparkles className={`w-6 h-6 mb-2 ${selectedBadges.includes('inspirer') ? 'text-indigo-500' : ''}`} />
                      <span className="font-bold text-sm">شارة المُلهِم</span>
                    </button>
                  </div>
                </div>

              </div>
              <div className="p-6 bg-slate-50 dark:bg-black/20 border-t border-slate-200/50 dark:border-white/5 flex flex-col-reverse sm:flex-row items-center justify-end gap-3 mt-auto sticky bottom-0">
                <button onClick={() => handleAction('needs_revision')} disabled={actionLoading} className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold bg-white dark:bg-zinc-800 text-rose-600 border border-slate-200 dark:border-white/10 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:border-rose-200 dark:hover:border-rose-500/30 transition-all flex items-center justify-center gap-2">
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <AlertCircle className="w-5 h-5" />} طلب تعديل
                </button>
                <button onClick={() => handleAction('graded')} disabled={actionLoading} className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold focus:outline-none focus:ring-4 focus:ring-primary-500/30 bg-primary-600 hover:bg-primary-500 text-white shadow-lg shadow-primary-500/30 transition-all active:scale-95 flex items-center justify-center gap-2">
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} اعتماد الدرجة
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
